import camelcaseKeys from 'camelcase-keys'

import ApiClient from '@src/lib/api_client'
import Activity from '@src/data/activity'
import { API } from '@src/lib/api_path'
import { HealthGoal, HealthIndicator } from '@src/data/goal'
import SessionManager from '@src/lib/session_manager'
import { sortExerciseTagsByCategory } from '@src/util/tag-ordering'

export class ExerciseTag {

  readonly id: number
  key: string
  type: string

  constructor (attributes) {
    Object.assign(this, attributes)
  }
}

type ExerciseTags = { exercise_tags: ExerciseTag[] }
type ActivitiesResponse = { activities: Activity[] }
type HealthGoalsResponse = { health_goals: HealthGoal[] }

export class DefaultValues {

  static loadExerciseTags (store?) {
    const apiClient: ApiClient = new ApiClient(SessionManager.Instance.isManager() ? API.TRAINERS_API.EXERCISE_TAGS : API.MEMBERS_API.EXERCISE_TAGS)
    return apiClient.get<ExerciseTags>()
      .then((response) => {
        let exerciseTags = response.exercise_tags
          .map((tag) => {
            tag.key = tag.key.toLowerCase()
            return new ExerciseTag(camelcaseKeys(tag))
          })
        let formattedExerciseTags = this.extractExerciseTagsData(exerciseTags)
        // Sort tags according to the defined order in tag-ordering.ts
        formattedExerciseTags = sortExerciseTagsByCategory(formattedExerciseTags)
        if (store) {
          store.dispatch('setExerciseTags', formattedExerciseTags)
          store.dispatch('setCompleteExerciseTags', exerciseTags)
        }
        return formattedExerciseTags
      })
      .catch((error) => {
        console.error('failed to retrieve exercise tags', error)
      })
  }

  static extractExerciseTagsData (response: ExerciseTag[]) {
    let categories = new Set<string>([])
    response.forEach(tag => {
      categories.add(tag.type)
    })

    let exerciseTags: any = {}
    categories.forEach(category => {
      let tags: string[] = []
      tags = response
        .filter(tag => tag.type === category)
        .map(tag => tag.key.replace('/', '_'))
      exerciseTags[category] = tags
    })
    // Note: Tags will be sorted by sortExerciseTagsByCategory in loadExerciseTags
    return exerciseTags
  }

  static loadActivities (store?) {
    const apiClient: ApiClient = new ApiClient(SessionManager.Instance.isManager() ? API.TRAINERS_API.ACTIVITIES : API.MEMBERS_API.ACTIVITIES)
    return apiClient.get<ActivitiesResponse>()
      .then((response: ActivitiesResponse) => {
        let activities = response.activities
          .sort((a, b) => a.key < b.key ? -1 : a.key > b.key ? 1 : 0)
          .map((activity: Activity) => new Activity(camelcaseKeys(activity, { deep: true })))
        if (store) {
          store.dispatch('setActivities', activities)
        }
        return activities
      })
      .catch((error) => {
        console.error('failed to retrieve activities', error)
      })
  }

  static loadPredefinedHealthGoals (store?) {
    if (SessionManager.Instance.isManager()) {
      const apiClient: ApiClient = new ApiClient(API.TRAINERS_API.HEALTH_GOALS)
      return apiClient.get<HealthGoalsResponse>()
        .then((response: HealthGoalsResponse) => {
          const healthGoals = response.health_goals.map((goal: HealthGoal) => {
            let formattedGoal = new HealthGoal(camelcaseKeys(goal, { deep: true }))
            formattedGoal.indicators = goal.indicators.map(indicator => new HealthIndicator(camelcaseKeys(indicator, { deep: true })))
            return formattedGoal
          })
          if (store) {
            store.dispatch('setPredefinedHealthGoals', healthGoals)
          }
          return healthGoals
        })
        .catch(error => console.log('cannot load default health goals', error))
    }
  }
}
